import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  apiRequestLimits,
  getTransactions,
  getTransactionsChartData,
  getVolumes,
  getPartnerDiscount,
} from "../store/actions";
import DataLoader from "../components/globalView/DataLoader";
import TransactionsChart from "../components/charts/TransactionsChart";
import PartnerTierCard from "../components/charts/PartnerTierCard";
import CoinProfitPieChart from "../components/charts/IncomePie";
import TableDataVolumes from "../components/tables/TableDataVolumes";
import { Helmet } from "react-helmet";
import TableDataHomeTransactions from "../components/tables/TableDataHomeTransactions";
import UnlinkedWalletsBanner from "../components/globalView/UnlinkedWalletsBanner";

const Dashboard = () => {
  const dispatch = useDispatch();

  const [tableOpen, setTableOpen] = useState("");
  const [monthsBefore, setMonthsBefore] = useState(0);
  const [apiStatus, setApiStatus] = useState("SUCCESS");

  const user = useSelector((state) => state.userReducer.user);
  const isLoading = useSelector((state) => state.userReducer.loading);
  const volumes = useSelector((state) => state.userReducer.volumes);
  const transactions = useSelector((state) => state.userReducer.transactions);
  const transactionsArray =
    transactions &&
    Object.keys(transactions).length > 0 &&
    transactions.transactions;
  const transactionsChartData = useSelector(
    (state) => state.userReducer.transactionsChartData
  );

  useEffect(() => {
    // Initial dashboard requests on mount
    dispatch(getTransactions());
    dispatch(getVolumes());
    dispatch(apiRequestLimits());
    dispatch(getPartnerDiscount());
  }, [dispatch]);

  useEffect(() => {
    // Only update chart data when monthsBefore changes
    setApiStatus("LOADING");
    dispatch(getTransactionsChartData(monthsBefore))
      .then((response) => {
        if (response && response.status === 200) {
          setApiStatus("SUCCESS");
        } else {
          setApiStatus("ERROR");
        }
      })
      .catch(() => {
        setApiStatus("ERROR");
      });
  }, [dispatch, monthsBefore]);

  const currentDiv = (id) => {
    setTableOpen(id);
  };

  const handlePrevMonth = () => {
    setApiStatus("LOADING");
    setMonthsBefore((prev) => prev + 1);
  };

  const handleNextMonth = () => {
    setApiStatus("LOADING");
    setMonthsBefore((prev) => prev - 1);
  };

  const getCurrentMonth = () => {
    const today = new Date();
    today.setMonth(today.getMonth() - monthsBefore);
    const year = today.getFullYear();
    const month = today.toLocaleString("default", { month: "long" });
    return `${month} ${year}`;
  };

  // Only disable buttons during loading; error mode does not disable them.
  const disableButtons = apiStatus === "LOADING";

  const calcDiff = (current, previous) => {
    if (previous === null || previous === undefined || previous === 0) return null;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  const formattedDiff = (current, previous) => {
    const diff = calcDiff(current, previous);
    if (diff === null) return null;
    const rounded = diff.toFixed(1);
    return (
      <div className={`kDelta${diff >= 0 ? "" : " down"}`}>
        {diff >= 0 ? "▲" : "▼"} {rounded}% <span className="muted">vs last month</span>
      </div>
    );
  };

  const kpis = [
    {
      label: "Total Orders",
      value: transactionsChartData?.currentMonth?.SumAnnualOrders ?? 0,
      delta: null,
    },
    {
      label: "Monthly Orders",
      value: transactionsChartData?.currentMonth?.SumCompletedMonthlyOrders ?? 0,
      delta:
        transactionsChartData?.previousMonth?.SumCompletedMonthlyOrders != null
          ? formattedDiff(
              transactionsChartData?.currentMonth?.SumCompletedMonthlyOrders,
              transactionsChartData?.previousMonth?.SumCompletedMonthlyOrders
            )
          : null,
    },
    {
      label: "Max Orders in a Day",
      value: transactionsChartData?.currentMonth?.maxCompletedOrdersNumOnADay ?? 0,
      delta:
        transactionsChartData?.previousMonth?.maxCompletedOrdersNumOnADay != null
          ? formattedDiff(
              transactionsChartData?.currentMonth?.maxCompletedOrdersNumOnADay,
              transactionsChartData?.previousMonth?.maxCompletedOrdersNumOnADay
            )
          : null,
    },
    {
      label: "Monthly Volume",
      value: transactionsChartData?.currentMonth?.monthlyVolume ?? 0,
      delta:
        transactionsChartData?.previousMonth?.monthlyVolume != null
          ? formattedDiff(
              transactionsChartData?.currentMonth?.monthlyVolume,
              transactionsChartData?.previousMonth?.monthlyVolume
            )
          : null,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Dashboard | Instaswap Partners</title>
      </Helmet>
      {isLoading && user && Object.keys(user).length > 0 ? (
        <DataLoader />
      ) : (
        <div>
          <UnlinkedWalletsBanner />

          {transactionsChartData && (
            <div className="kRow">
              {kpis.map((k) => (
                <div className="kCard" key={k.label}>
                  <div className="kTop">
                    <span className="kLbl">{k.label}</span>
                    <span className="kIco">◈</span>
                  </div>
                  <div className="kVal">{k.value}</div>
                  {k.delta}
                </div>
              ))}
            </div>
          )}

          <PartnerTierCard />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.6fr 1fr",
              gap: 16,
              marginTop: 16,
              alignItems: "stretch",
            }}
            className="dash-grid2"
          >
            <div className="panel" style={{ display: "flex", flexDirection: "column" }}>
              <div className="pHd">
                <div>
                  <div className="pTitle">Orders &amp; volume</div>
                  <div className="pSub">{getCurrentMonth()}</div>
                </div>
                <div className="seg">
                  {apiStatus !== "ERROR" && (
                    <button
                      type="button"
                      className="segB"
                      onClick={handlePrevMonth}
                      disabled={disableButtons}
                    >
                      ‹ Prev
                    </button>
                  )}
                  <button type="button" className="segB on" disabled>
                    {getCurrentMonth()}
                  </button>
                  {monthsBefore > 0 && (
                    <button
                      type="button"
                      className="segB"
                      onClick={handleNextMonth}
                      disabled={disableButtons}
                    >
                      Next ›
                    </button>
                  )}
                </div>
              </div>
              {apiStatus === "LOADING" ? (
                <div
                  style={{
                    flex: 1,
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DataLoader />
                </div>
              ) : (
                volumes &&
                Object.keys(volumes).length > 0 && (
                  <div style={{ flex: 1, minHeight: 300 }}>
                    <TransactionsChart />
                  </div>
                )
              )}
            </div>

            <div className="panel">
              <div className="pHd">
                <div>
                  <div className="pTitle">Total income</div>
                  <div className="pSub">Profit per coin, all-time</div>
                </div>
                {volumes && Object.keys(volumes).length > 0 && (
                  <span className="pill done">
                    <span className="pd" />≅ {volumes.sumBtcVolume.toFixed(1)} $
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "6px 0 2px",
                }}
              >
                <CoinProfitPieChart />
              </div>
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 6,
                  borderTop: "1px solid rgba(255,255,255,.06)",
                  maxHeight: 300,
                  overflowY: "auto",
                }}
              >
                {(() => {
                  const coins = (volumes.calculatedVolumesPerCoin || [])
                    .slice()
                    .sort((a, b) => (Number(b.SumUSDProfit) || 0) - (Number(a.SumUSDProfit) || 0));
                  const maxUsd = Number(coins[0]?.SumUSDProfit) || 0;
                  return coins.map((item) => (
                    <TableDataVolumes key={item.id} item={item} maxUsd={maxUsd} />
                  ));
                })()}
              </div>
            </div>
          </div>

          <div className="panel" style={{ marginTop: 16, padding: "20px 20px 8px" }}>
            <div className="pHd">
              <div>
                <div className="pTitle">Recent swaps</div>
                <div className="pSub">Your latest transactions</div>
              </div>
            </div>
            <div style={{ margin: "8px -20px 0", overflowX: "auto" }}>
              <div style={{ minWidth: 700 }}>
                {transactionsArray && transactionsArray.length > 0 ? (
                  transactionsArray
                    .slice(0, 5)
                    .map((item) => (
                      <TableDataHomeTransactions
                        key={item.id}
                        item={item}
                        tableOpen={tableOpen}
                        currentDiv={currentDiv}
                      />
                    ))
                ) : (
                  <div className="gtEmpty">No transactions yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
